import React, { useEffect, useRef, useState } from 'react';
import { EraserIcon, PenLineIcon, RotateCcwIcon, Undo2Icon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
interface Stroke {
  points: {
    x: number;
    y: number;
  }[];
  color: string;
  width: number;
  erase: boolean;
}
const COLORS = ['#0f172a', '#0f766e', '#b91c1c', '#1d4ed8', '#a16207'];
const WIDTHS = [2, 4, 8];

/**
 * Drawing and handwriting. Pointer-based so a finger, stylus or mouse all work.
 * Saved as a PNG attachment record against the note, never inlined into text.
 */
export function DrawingCanvas({
  open,
  onClose,
  onSave
}: {
  open: boolean;
  onClose: () => void;
  onSave: (dataUrl: string, strokeCount: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [color, setColor] = useState(COLORS[0]);
  const [width, setWidth] = useState(WIDTHS[1]);
  const [erasing, setErasing] = useState(false);
  const drawing = useRef(false);
  useEffect(() => {
    if (!open) {
      setStrokes([]);
      setErasing(false);
    }
  }, [open]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    strokes.forEach(stroke => {
      if (stroke.points.length < 2) return;
      context.beginPath();
      context.strokeStyle = stroke.erase ? '#ffffff' : stroke.color;
      context.lineWidth = stroke.erase ? stroke.width * 4 : stroke.width;
      stroke.points.forEach((point, index) => {
        if (index === 0) context.moveTo(point.x, point.y);else context.lineTo(point.x, point.y);
      });
      context.stroke();
    });
  }, [strokes, open]);
  function positionOf(event: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / rect.width * canvas.width,
      y: (event.clientY - rect.top) / rect.height * canvas.height
    };
  }
  return <Modal open={open} onClose={onClose} title="Draw or handwrite" description="Sketch a diagram or write by hand. Saved to this note as an image attachment." width="lg" footer={<>
          <Button variant="ghost" onClick={onClose} data-id="element-3400" data-mp="2aru1updcbt84">
            Cancel
          </Button>
          <Button variant="primary" disabled={strokes.length === 0} onClick={() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      onSave(canvas.toDataURL('image/png'), strokes.length);
      onClose();
    }} data-id="element-3401" data-mp="25uk1ieoexx5i">
            Save to note
          </Button>
        </>} data-id="element-3399" data-mp="1i2wjkm94pwfd">
      <div className="space-y-3" data-id="element-3402" data-mp="2or21tkhbv5dd">
        <div className="flex flex-wrap items-center gap-2" data-id="element-3403" data-mp="2oq0f2iqy8uft">
          <Button size="sm" variant={erasing ? 'secondary' : 'primary'} icon={<PenLineIcon className="h-3.5 w-3.5" data-id="element-3405" data-mp="g9lbk2s2a692" />} onClick={() => setErasing(false)} data-id="element-3404" data-mp="baijrk13dwj1">
            Pen
          </Button>
          <Button size="sm" variant={erasing ? 'primary' : 'secondary'} icon={<EraserIcon className="h-3.5 w-3.5" data-id="element-3407" data-mp="p6vqby438diq" />} onClick={() => setErasing(true)} data-id="element-3406" data-mp="kci1kejxl4n1">
            Eraser
          </Button>

          <span className="mx-1 h-5 w-px bg-line" aria-hidden="true" data-id="element-3408" data-mp="z6e6e3gvnf5i" />

          {COLORS.map(option => <button key={option} type="button" aria-label={`Colour ${option}`} aria-pressed={color === option && !erasing} onClick={() => {
          setColor(option);
          setErasing(false);
        }} style={{
          backgroundColor: option
        }} className={`h-6 w-6 rounded-full border-2 transition-transform duration-150 ease-out ${color === option && !erasing ? 'border-ink scale-110' : 'border-transparent'}`} data-id="element-3409" data-mp="13zfmufwqfmr7" />)}

          <span className="mx-1 h-5 w-px bg-line" aria-hidden="true" data-id="element-3410" data-mp="1e19q5nbr2hzl" />

          {WIDTHS.map(option => <button key={option} type="button" aria-label={`Stroke width ${option}`} aria-pressed={width === option} onClick={() => setWidth(option)} className={`flex h-7 w-7 items-center justify-center rounded-md border transition-colors duration-150 ease-out ${width === option ? 'border-brand-700 bg-brand-50' : 'border-line-strong hover:border-ink-subtle'}`} data-id="element-3411" data-mp="19384cumxw4kf">
              <span className="rounded-full bg-ink" style={{
            width: option + 2,
            height: option + 2
          }} data-id="element-3412" data-mp="26omb5t29ihqs" />
            </button>)}

          <span className="ml-auto flex gap-1.5" data-id="element-3413" data-mp="299k771l9lnsv">
            <Button size="sm" variant="ghost" icon={<Undo2Icon className="h-3.5 w-3.5" data-id="element-3415" data-mp="2earihamm954a" />} disabled={strokes.length === 0} onClick={() => setStrokes(current => current.slice(0, -1))} data-id="element-3414" data-mp="29c6h37vmofe9">
              Undo
            </Button>
            <Button size="sm" variant="ghost" icon={<RotateCcwIcon className="h-3.5 w-3.5" data-id="element-3417" data-mp="2s1d251jw9j38" />} disabled={strokes.length === 0} onClick={() => setStrokes([])} data-id="element-3416" data-mp="24ctveqghted9">
              Clear
            </Button>
          </span>
        </div>

        <canvas ref={canvasRef} width={960} height={540} aria-label="Drawing surface" onPointerDown={event => {
        event.currentTarget.setPointerCapture(event.pointerId);
        drawing.current = true;
        const point = positionOf(event);
        setStrokes(current => [...current, {
          points: [point],
          color,
          width,
          erase: erasing
        }]);
      }} onPointerMove={event => {
        if (!drawing.current) return;
        const point = positionOf(event);
        setStrokes(current => {
          const next = [...current];
          const last = next[next.length - 1];
          if (last) next[next.length - 1] = {
            ...last,
            points: [...last.points, point]
          };
          return next;
        });
      }} onPointerUp={() => {
        drawing.current = false;
      }} onPointerLeave={() => {
        drawing.current = false;
      }} className="w-full cursor-crosshair touch-none rounded-lg border border-line-strong bg-white" data-id="element-3418" data-mp="2n9v00a64xcgo" />

        <p className="text-[12px] leading-5 text-ink-muted" data-id="element-3419" data-mp="2nwsp0l92wrdt">
          {strokes.length} stroke{strokes.length === 1 ? '' : 's'}. Handwriting recognition would run
          over this image server-side; the drawing itself is stored and searchable by its filename now.
        </p>
      </div>
    </Modal>;
}